import type { IconType } from '../Icon';
import Button from './Button';
import ButtonLink from './ButtonLink';

/**
 * Button variants.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary';

/**
 * Button icons.
 */

export type ButtonIcon = IconType | 'none';

/**
 * Type definition of the button variants properties.
 *
 * Using a discriminated union, we can change the properties based on the variant.
 */

type BaseButtonProps = {
  xl?: boolean;
  active?: boolean;
  activeColor?: 'orange-2' | 'orange-1' | 'terra-3' | 'teal-1';
  disabled?: boolean;
  unstyled?: boolean;
  className?: string;
};

/**
 * Type definition for the `primary` button variant.
 *
 * Specific properties can be added here thanks to the discriminated union.
 */

type ButtonPrimaryProps = BaseButtonProps & {
  variant: 'primary';
  icon?: ButtonIcon;
};

/**
 * Type definition for the `secondary` button variant.
 *
 * Specific properties can be added here thanks to the discriminated union.
 */

type ButtonSecondaryProps = BaseButtonProps & {
  variant: 'secondary';
};

/**
 * Type definition for the `tertiary` button variant.
 *
 * Specific properties can be added here thanks to the discriminated union.
 */

type ButtonTertiaryProps = BaseButtonProps & {
  variant: 'tertiary';
  icon?: ButtonIcon;
};

/**
 * Type definition of the button properties.
 */

export type ButtonProps = ButtonPrimaryProps | ButtonSecondaryProps | ButtonTertiaryProps;

/**
 * Helper function to get the default icon based on the button variant.
 *
 * @param variant - The button variant.
 *
 * @returns The default icon for the button variant.
 */

export const getButtonDefaultIcon = (variant: ButtonProps['variant']): ButtonIcon => {
  switch (variant) {
    case 'primary':
      return 'arrowRight';
    default:
    case 'tertiary':
      return 'none';
  }
};

export { Button, ButtonLink };
